import React from 'react';
import { createPortal } from 'react-dom';
import { FaTimes } from 'react-icons/fa';
import ReadOnlyMedicineCartItem from './ReadOnlyMedicineCartItem';
import ReadOnlyCartSummary from './ReadOnlyCartSummary';
import PrescriptionAttachments from './PrescriptionAttachments';
import OrderNoteCard from './OrderNoteCard';
import DeliveryAddressCard from '../common/DeliveryAddressCard';
import { currencyRounded, formatDateTime, getStatusBadgeStyle, maskMobile } from '../../utils/subscriberHelpers';

// The medicine module's "Booking Details" popup, moved out of MedicineBookingProfile so the
// subscriber portal can open the SAME view without navigating away from the subscriber page.
// Purely presentational: every piece of state and every fetch stays with the host page, and
// the mutating actions (refund, status change) live on the module's list rows, not in here.
//
// `showFullMobile` is the whole mobile-masking policy for this popup and both hosts pass
// false — the module page has always masked (its `mobilesUnmasked` is a frozen `useState(false)`)
// and the subscriber page must not widen that just because its own list has an unmask toggle.
//
// `getStatusStyle` defaults to the canonical palette but stays overridable, because
// MedicineBookingProfile has its own local palette that also styles its list rows — passing
// that one in keeps this extraction pixel-identical on the module page.
const MedicineBookingDetailModal = ({
  mounted,
  bookingDetail,
  loading,
  error,
  onRetry,
  onClose,
  showFullMobile = false,
  partnerDataExpanded,
  onTogglePartnerData,
  getStatusStyle = getStatusBadgeStyle,
}) => {
  if (!mounted) return null;

  return createPortal(
    <div 
      className="fixed top-0 left-0 right-0 bottom-0 w-screen h-screen z-[9999] flex items-center justify-center p-4 bg-black bg-opacity-50" 
      onClick={onClose}
      style={{ margin: 0, padding: '1rem' }}
    >
      <div 
        className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-6 border-b border-gray-200 bg-gray-50">
          <h2 className="text-xl font-semibold text-gray-900">Booking Details</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close modal"
          >
            <FaTimes className="w-5 h-5" />
          </button>
        </div>
        
        {/* Modal Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <div className="text-red-600 mb-2">{error}</div>
              <button
                onClick={onRetry}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Retry
              </button>
            </div>
          ) : bookingDetail ? (
            <div className="space-y-6">
              {/* Main Details Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <div className="text-xs text-gray-500 mb-1">Date & Time</div>
                  <div className="text-sm font-medium text-gray-900">
                    {(() => {
                      const dateTimeValue = bookingDetail.date_time;
                      // Ensure we never render an object directly
                      if (typeof dateTimeValue === 'object' && dateTimeValue !== null && !(dateTimeValue instanceof Date)) {
                        return formatDateTime(dateTimeValue);
                      }
                      return formatDateTime(dateTimeValue);
                    })()}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">Subscriber Name</div>
                  <div className="text-sm font-medium text-gray-900">{bookingDetail.subscriber_name || 'N/A'}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">Mobile Number</div>
                  <div className="text-sm font-medium text-gray-900">
                    {!showFullMobile
                      ? maskMobile(bookingDetail.mobile_number)
                      : (bookingDetail.mobile_number || 'N/A')}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">REF CODE</div>
                  <div className="text-sm font-medium text-gray-900">{bookingDetail.ref_code || 'N/A'}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">Service Provider</div>
                  <div className="text-sm font-medium text-gray-900">{bookingDetail.service_provider || 'N/A'}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">Price</div>
                  <div className="text-sm font-medium text-gray-900">
                    ₹{(bookingDetail.price || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">Cashback</div>
                  <div className="text-sm font-medium text-gray-900">
                    {currencyRounded(bookingDetail.cashback || 0)}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">EC (Eligible Cashback)</div>
                  <div className="text-sm font-medium text-gray-900">
                    {currencyRounded(bookingDetail.eligible_cashback || 0)}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">RC (Redeemable Cashback)</div>
                  <div className="text-sm font-medium text-gray-900">
                    {currencyRounded(bookingDetail.redeemable_cashback || 0)}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">WB (Wallet Balance)</div>
                  <div className="text-sm font-medium text-gray-900">
                    {currencyRounded(bookingDetail.wallet_balance || 0)}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">Family Members</div>
                  <div className="text-sm font-medium text-gray-900" title={bookingDetail.family_member_names || ''}>
                    {bookingDetail.family_member_names || 'N/A'}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">Status</div>
                  <span 
                    className="inline-flex px-2 py-1 text-xs font-semibold rounded-full border justify-center"
                    style={getStatusStyle(bookingDetail.status)}
                  >
                    {(() => {
                      const statusValue = bookingDetail.status || 'N/A';
                      const statusLower = String(statusValue).toLowerCase();
                      // Map "out_of_stock" to "Out of Stock" for display
                      if (statusLower === 'out_of_stock') {
                        return 'Out of Stock';
                      }
                      return statusValue;
                    })()}
                  </span>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">Payment Method</div>
                  <div className="text-sm font-medium text-gray-900">{bookingDetail.payment_method || 'N/A'}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">AP Transaction ID</div>
                  <div className="text-sm font-medium text-gray-900 font-mono">{bookingDetail.ap_transaction_id || 'N/A'}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">3rd Party Booking Ref</div>
                  <div className="text-sm font-medium text-gray-900">{bookingDetail.third_party_booking_ref_code || 'N/A'}</div>
                </div>
                {bookingDetail.booking_id && (
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Booking ID</div>
                    <div className="text-sm font-medium text-gray-900 font-mono">{bookingDetail.booking_id}</div>
                  </div>
                )}
              </div>

              {/* Delivery Address Section — shared with the diagnostic snapshot */}
              <DeliveryAddressCard
                address={bookingDetail.delivery_address}
                title="Delivery Address"
                className="border-t border-gray-200 pt-4"
              />

              {/* Prescription Section — files the subscriber attached to
                  this booking. View / download only; the subscriber owns
                  attaching and removing them. */}
              <div className="border-t border-gray-200 pt-4">
                <PrescriptionAttachments documents={bookingDetail.prescriptions} />
              </div>

              {Array.isArray(bookingDetail.partner_data) && bookingDetail.partner_data
                .filter(orderData => orderData?.order_note && String(orderData.order_note).trim())
                .map((orderData, orderIndex) => (
                  <div key={`note-${orderData.order_id || orderIndex}`} className="border-t border-gray-200 pt-4">
                    <OrderNoteCard note={String(orderData.order_note)} />
                  </div>
                ))}

              {/* Partner Data Section */}
              {bookingDetail && (
                <div className="border-t border-gray-200 pt-4">
                  <div
                    className="text-sm font-semibold text-gray-700 cursor-pointer hover:text-gray-900 flex items-center"
                    onClick={onTogglePartnerData}
                  >
                    <span className="mr-2">Partner Data</span>
                    <span className="text-xs text-gray-500 font-normal">(Click to expand)</span>
                    {partnerDataExpanded ? (
                      <svg className="w-4 h-4 ml-2 text-gray-500 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4 ml-2 text-gray-500 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    )}
                  </div>
                  {partnerDataExpanded && (
                    <div className="mt-3">
                      {bookingDetail.partner_data !== undefined && bookingDetail.partner_data !== null ? (
                        Array.isArray(bookingDetail.partner_data) && bookingDetail.partner_data.length > 0 ? (
                          <div className="space-y-4">
                            {bookingDetail.partner_data.map((orderData, orderIndex) => (
                              <div key={orderData.order_id || orderIndex} className="border border-gray-200 rounded-md p-4 bg-white">
                                {/* Cart Items */}
                                {orderData.cart_items && Array.isArray(orderData.cart_items) && orderData.cart_items.length > 0 ? (
                                  <div className="space-y-3 mb-4">
                                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Cart Items:</h4>
                                    {orderData.cart_items
                                      .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
                                      .map((item) => (
                                        <ReadOnlyMedicineCartItem key={item.id || item.service_item_id} item={item} />
                                      ))}
                            </div>
                                ) : (
                                  <div className="mb-4 text-sm text-gray-500 text-center py-4">
                                    No cart items available
                            </div>
                                )}
                                
                                {/* Cart Summary */}
                                <ReadOnlyCartSummary orderData={orderData} finalPrice={bookingDetail?.price} />
                              </div>
                            ))}
                        </div>
                        ) : (
                          <div className="p-6 bg-gray-50 rounded-md border border-gray-200 text-sm text-gray-500 text-center">
                            No cart details available. Cart details are only shown for orders with status: out_of_stock, processed, ordered, in_progress, delivered, success, complete, cancelled, or failed.
                          </div>
                        )
                      ) : (
                        <div className="p-6 bg-gray-50 rounded-md border border-gray-200 text-sm text-gray-500 text-center">
                          No partner data available
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : null}
        </div>
        
        {/* Modal Footer */}
        <div className="flex justify-end px-6 py-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default MedicineBookingDetailModal;
